import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Camera,
  Check,
  Circle,
  HelpCircle,
  Landmark,
  ListChecks,
  PartyPopper,
  ShieldCheck,
  Truck,
  Wallet,
} from "lucide-react";

const STORAGE_KEY = "mttApp:parent-help-progress";

const QUICK_START_PHASES = [
  {
    id: "phase-account-security",
    eyebrow: "Phase 1",
    title: "Account Creation & Security",
    description: "Set up the grown-up account, verify identity, and give your child a safe Kid Zone profile.",
    icon: ShieldCheck,
    tone: "teal",
    steps: [
      { id: "step-home-screen-bookmark", title: "Use the Home screen shortcut prompt", detail: "Open MyTableTopApp in your browser and tap Add to Home Screen when prompted so it feels like an app." },
      { id: "step-sign-up", title: "Sign up for a parent account", detail: "Create the adult account with your name, email address, mobile number, and a secure password." },
      { id: "step-email-verification", title: "Verify your email address", detail: "Open the verification email and tap the link so receipts, alerts, and password resets reach you." },
      { id: "step-security-setup", title: "Complete the 2-sec security setup", detail: "Confirm the quick security prompt so sensitive parent actions stay protected." },
      { id: "step-id-selfie-dob", title: "Complete ID Selfie & DOB verification", detail: "Finish the adult identity check used to keep the marketplace safe for families." },
      { id: "step-child-profile-pin", title: "Set up a child profile with a 4-digit PIN", detail: "Add your child's nickname, age bracket, avatar, and the PIN that protects Kid Zone." },
      { id: "step-kid-zone-login", title: "Log in to Kid Zone", detail: "Sign in with your child's profile so they can start creating listings safely." },
    ],
  },
  {
    id: "phase-listing-approval",
    eyebrow: "Phase 2",
    title: "Listing & Approval",
    description: "Turn outgrown toys into parent-approved marketplace listings.",
    icon: Camera,
    tone: "pink",
    steps: [
      { id: "step-child-creates-listing", title: "Child creates a listing with GBP pricing", detail: "Your child photographs the toy, adds a title and description, and chooses a fair pound price." },
      { id: "step-parent-approval-alert", title: "Watch for the Parent Dashboard approval alert", detail: "Every new listing lands in your approval queue before buyers can see it." },
      { id: "step-live-publishing", title: "Approve live marketplace publishing", detail: "Review the photos, price, and details, then approve the listing to make it public." },
    ],
  },
  {
    id: "phase-sale-payment-shipping",
    eyebrow: "Phase 3",
    title: "Order Fulfillment",
    description: "Follow the order from buyer checkout through manual dispatch and payout release.",
    icon: Truck,
    tone: "teal",
    steps: [
      { id: "step-buyer-stripe-checkout", title: "Buyer completes checkout", detail: "The buyer pays the item price, buyer protection fee, Stripe processing, and £3.50 Royal Mail postage at checkout." },
      { id: "step-sale-notifications", title: "Parent and child receive order notifications", detail: "You both get an update when the item sells so everyone knows what happens next." },
      { id: "step-royal-mail-fulfilment", title: "Ship via Manual dispatch", detail: "Buy postage directly at the Post Office counter or online, then enter the carrier name and tracking/reference number in Manual Dispatch." },
    ],
    note: "Note for Sellers: You will pay for postage upfront when dispatching manually. Entering a valid Tracking/Reference Number is required. Once you submit tracking details and click 'Mark as Posted', the £3.50 buyer postage payment will be released back to your Parent Wallet along with your item earnings!",
  },
  {
    id: "phase-wallet-transfers",
    eyebrow: "Phase 4",
    title: "Wallet & BACS Transfers",
    description: "Move purchasing funds and profits between the family wallets.",
    icon: Wallet,
    tone: "pink",
    steps: [
      { id: "step-top-up-account", title: "Top up the Household Account", detail: "Add funds to the parent-controlled account before sending spending money to a child." },
      { id: "step-transfer-to-child", title: "Transfer allowance funds to your child", detail: "Move a chosen amount into the child wallet so they can buy from other families." },
      { id: "step-receive-child-profits", title: "Transfer child profits back to the parent account", detail: "Move sale profits back to the Household Account when you are ready to manage or withdraw them." },
      { id: "step-bacs-payout", title: "Send an external BACS payout", detail: "Send available household funds to your linked bank account from the Parent Wallet." },
    ],
  },
  {
    id: "phase-stripe-kyc",
    eyebrow: "Phase 5",
    title: "Stripe Connect & KYC Verification",
    description: "Connect the payout bank account with the right Stripe details.",
    icon: Landmark,
    tone: "teal",
    steps: [
      { id: "step-link-bank", title: "Link your bank account", detail: "Start Stripe Connect from the Parent Dashboard payout card." },
      { id: "step-business-type", title: "Select Individual / Sole Trader", detail: "Choose this option for a parent receiving marketplace payouts." },
      { id: "step-stripe-website", title: "Set the website to www.mytabletopapp.co.uk", detail: "Use the marketplace website address when Stripe asks where you sell." },
      { id: "step-bank-details", title: "Add your Sort Code and Account Number", detail: "Enter the UK bank account that should receive your BACS payouts." },
      { id: "step-submit-verification", title: "Submit verification", detail: "Send the details to Stripe and wait for the account checks to complete." },
    ],
  },
  {
    id: "phase-rewards-wallet",
    eyebrow: "Phase 6",
    title: "Rewards & Wallet Updates",
    description: "Celebrate the sale and track every pound in the family ledger.",
    icon: PartyPopper,
    tone: "pink",
    steps: [
      { id: "step-wallet-passbook-updates", title: "Review Passbook ledger updates", detail: "Sale, transfer, postage, and payout entries appear in the family Wallet and Passbook as they happen." },
      { id: "step-first-sale-celebration", title: "Enjoy the First Sale celebration modal", detail: "Your child sees a confetti celebration and earns badges as their shop reaches milestones." },
    ],
  },
];

const FAQ_ITEMS = [
  {
    id: "faq-listing-review",
    question: "Why isn't my child's listing showing up in the shop yet?",
    answer: "Every listing requires parent review and approval from your Parent Dashboard before it goes live to buyers.",
  },
  {
    id: "faq-postage-cost",
    question: "How does postage work, and who pays for it?",
    answer: "Buyers pay £3.50 for postage at checkout. Sellers purchase their postage directly (at the Post Office counter or online) and enter the tracking number via Manual Dispatch. Upon entering a valid tracking number and marking the order as posted, the full item earnings + £3.50 postage reimbursement are released into your Parent Wallet.",
  },
  {
    id: "faq-no-printer",
    question: "What if I don't have a printer for shipping labels?",
    answer: "Automated Royal Mail label generation is coming soon. For now, purchase postage at the Post Office counter or online, ask for the tracking/reference number, and enter it via Manual Dispatch on the order details page.",
  },
  {
    id: "faq-bank-payout",
    question: "How do I move money from the app into my bank account?",
    answer: "Go to your Parent Wallet, transfer profits from your Household Account, and select BACS Payout to send funds to your bank account.",
  },
  {
    id: "faq-stripe-business-type",
    question: "What business type should I choose during Stripe setup?",
    answer: "Select 'Individual / Sole Trader' and list www.mytabletopapp.co.uk as the website address.",
  },
];

const TONE_CLASSES = {
  teal: {
    icon: "bg-brandTeal/15 text-brandTeal",
    border: "data-[state=open]:border-brandTeal/30",
    progress: "bg-brandTeal",
  },
  pink: {
    icon: "bg-brandPink/15 text-brandPink",
    border: "data-[state=open]:border-brandPink/30",
    progress: "bg-brandPink",
  },
};

function StepCheckItem({ step, done, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(step.id)}
      aria-pressed={done}
      className={`w-full text-left flex items-start gap-3 rounded-2xl border px-4 py-3 transition-all ${done ? "border-emerald-200 bg-emerald-50/70" : "border-slate-200 bg-white hover:border-brandTeal/40 hover:bg-slate-50"}`}
      data-testid={`parent-help-step-${step.id}`}
    >
      <span className={`mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border ${done ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 bg-white text-slate-400"}`}>
        {done ? <Check className="h-4 w-4" /> : <Circle className="h-3.5 w-3.5" />}
      </span>
      <span className="min-w-0">
        <span className={`block text-sm font-bold ${done ? "text-emerald-950" : "text-slate-900"}`}>{step.title}</span>
        <span className={`mt-1 block text-sm leading-relaxed ${done ? "text-emerald-900/75" : "text-slate-600"}`}>{step.detail}</span>
      </span>
    </button>
  );
}

function PhaseAccordionItem({ phase, completedSteps, onToggleStep }) {
  const Icon = phase.icon;
  const tone = TONE_CLASSES[phase.tone];
  const doneCount = phase.steps.filter((step) => completedSteps.has(step.id)).length;
  const percent = Math.round((doneCount / phase.steps.length) * 100);

  return (
    <AccordionItem
      value={phase.id}
      className={`card-base mb-4 overflow-hidden border-b-0 px-0 ${tone.border}`}
      data-testid={`parent-help-${phase.id}`}
    >
      <AccordionTrigger
        className="px-5 py-5 hover:no-underline md:px-6"
        data-testid={`parent-help-${phase.id}-trigger`}
      >
        <div className="flex min-w-0 flex-1 items-start gap-4 pr-3 text-left">
          <div className={`inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl ${tone.icon}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{phase.eyebrow}</div>
            <div className="mt-1 font-display text-xl font-semibold text-brandSlate">{phase.title}</div>
            <p className="mt-1 text-sm font-normal leading-relaxed text-slate-600">{phase.description}</p>
            <div className="mt-3 flex items-center gap-3">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
                <div className={`h-full rounded-full transition-all ${tone.progress}`} style={{ width: `${percent}%` }} />
              </div>
              <span className="text-xs font-bold text-slate-500" data-testid={`parent-help-${phase.id}-count`}>
                {doneCount}/{phase.steps.length}
              </span>
            </div>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-5 md:px-6">
        <div className="space-y-3 border-t border-slate-100 pt-4">
          {phase.steps.map((step) => (
            <StepCheckItem
              key={step.id}
              step={step}
              done={completedSteps.has(step.id)}
              onToggle={onToggleStep}
            />
          ))}
          {phase.note && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900" data-testid={`parent-help-${phase.id}-note`}>
              {phase.note}
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export default function ParentHelpCenter() {
  const allStepIds = useMemo(
    () => QUICK_START_PHASES.flatMap((phase) => phase.steps.map((step) => step.id)),
    []
  );
  const [completedSteps, setCompletedSteps] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      return Array.isArray(parsed) ? parsed.filter((id) => allStepIds.includes(id)) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(completedSteps));
    } catch { /* localStorage unavailable */ }
  }, [completedSteps]);

  const completedStepSet = useMemo(() => new Set(completedSteps), [completedSteps]);
  const completedCount = allStepIds.filter((id) => completedStepSet.has(id)).length;
  const progressPercent = Math.round((completedCount / allStepIds.length) * 100);

  const toggleStep = (stepId) => {
    setCompletedSteps((current) =>
      current.includes(stepId) ? current.filter((id) => id !== stepId) : [...current, stepId]
    );
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]" data-testid="parent-help-page">
      <Navbar />
      <section className="mx-auto max-w-5xl px-6 pb-20 pt-10 lg:px-8 lg:pt-14">
        <Link
          to="/dashboard/parent"
          className="inline-flex items-center gap-1 text-sm font-semibold text-brandTeal hover:text-brandTealDark"
          data-testid="parent-help-dashboard-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Parent Dashboard
        </Link>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_320px] lg:items-end">
          <div>
            <div className="pill bg-brandTeal/15 text-brandTeal" data-testid="parent-help-eyebrow">
              <HelpCircle className="mr-1.5 h-3.5 w-3.5" /> Start Up Guide &amp; FAQ
            </div>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] text-brandSlate md:text-5xl lg:text-6xl" data-testid="parent-help-title">
              Start Up Guide &amp; FAQ
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
              Set up your family shop with confidence using a practical checklist for your first listing, first sale, first payout, and the questions parents ask most.
            </p>
          </div>

          <div className="card-base p-6" data-testid="parent-help-progress-card">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-brandPink">Setup progress</div>
                <div className="mt-1 font-display text-3xl font-semibold text-brandSlate" data-testid="parent-help-progress-count">
                  {completedCount}/{allStepIds.length}
                </div>
              </div>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brandPink/15 text-brandPink">
                <ListChecks className="h-6 w-6" />
              </div>
            </div>
            <div className="mt-4 h-3 overflow-hidden rounded-full bg-slate-100" data-testid="parent-help-progress-bar">
              <div className="h-full rounded-full bg-gradient-to-r from-brandTeal to-brandPink transition-all" style={{ width: `${progressPercent}%` }} />
            </div>
            <div className="mt-2 text-xs font-semibold text-slate-500" data-testid="parent-help-progress-percent">{progressPercent}% complete</div>
            {completedCount > 0 && (
              <button
                type="button"
                onClick={() => setCompletedSteps([])}
                className="mt-4 text-xs font-bold text-slate-500 underline underline-offset-4 hover:text-brandPink"
                data-testid="parent-help-reset-progress"
              >
                Reset progress
              </button>
            )}
          </div>
        </div>

        <section className="mt-12" data-testid="parent-help-quick-start">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-brandTeal">Section 1</div>
              <h2 className="mt-1 font-display text-3xl font-semibold text-brandSlate md:text-4xl">Quick Start Guide</h2>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold text-slate-600 shadow-sm" data-testid="parent-help-step-total">
              <BadgeCheck className="h-4 w-4 text-brandTeal" /> 24 steps · 6 phases
            </div>
          </div>

          <Accordion type="multiple" defaultValue={[QUICK_START_PHASES[0].id]} className="w-full" data-testid="parent-help-phase-accordion">
            {QUICK_START_PHASES.map((phase) => (
              <PhaseAccordionItem
                key={phase.id}
                phase={phase}
                completedSteps={completedStepSet}
                onToggleStep={toggleStep}
              />
            ))}
          </Accordion>
        </section>

        <section className="mt-14" data-testid="parent-help-faqs">
          <div className="mb-5">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-brandPink">Section 2</div>
            <h2 className="mt-1 font-display text-3xl font-semibold text-brandSlate md:text-4xl">Frequently Asked Questions</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600 md:text-base">
              Direct answers for approvals, postage, payouts, and Stripe setup.
            </p>
          </div>

          <Accordion type="single" collapsible defaultValue={FAQ_ITEMS[0].id} className="w-full" data-testid="parent-help-faq-accordion">
            {FAQ_ITEMS.map((item) => (
              <AccordionItem
                key={item.id}
                value={item.id}
                className="card-base mb-3 overflow-hidden border-b-0"
                data-testid={`parent-help-${item.id}`}
              >
                <AccordionTrigger className="px-5 py-5 text-left hover:no-underline md:px-6" data-testid={`parent-help-${item.id}-trigger`}>
                  <span className="pr-4 font-display text-lg font-semibold text-brandSlate">{item.question}</span>
                </AccordionTrigger>
                <AccordionContent className="px-5 md:px-6">
                  <p className="border-t border-slate-100 pt-4 text-sm leading-relaxed text-slate-600 md:text-base" data-testid={`parent-help-${item.id}-answer`}>
                    {item.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <div className="card-base mt-12 flex flex-wrap items-center justify-between gap-4 bg-gradient-to-br from-brandTeal/10 via-white to-brandPink/10 p-6 md:p-8" data-testid="parent-help-next-card">
          <div className="flex items-start gap-4">
            <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-brandTeal text-white">
              <BadgeCheck className="h-6 w-6" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-semibold text-brandSlate">Ready to finish setup?</h2>
              <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-600">
                Return to your dashboard to approve listings, link payouts, or jump into Kid Zone with your child.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/dashboard/parent" className="btn-primary" data-testid="parent-help-open-dashboard">
              Open Parent Dashboard <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/kidzone" className="btn-secondary" data-testid="parent-help-open-kidzone">
              Open Kid Zone
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
